/**
 * SB5-B.1 — projects/v1@2.1.0 contracts wire types.
 *
 * Hand-authored to mirror the canonical contract at
 * `_BACKEND/contracts/projects/v1/openapi.yaml` (schemas
 * `ContractListItem`, `ContractListResponse`, `ContractState`,
 * `ContractsFacetOption`, `ContractsFacetResponse`,
 * `ContractsSortKey`). The FE has no openapi-typescript codegen for
 * projects/v1 today — the Projects feature established the hand-roll
 * pattern; SB5-B continues it.
 *
 * Per Q14: ContractState comes from a SINGLE place in FE-land — this
 * file. The 8 canonical values mirror the BE enum verbatim. Do not
 * re-declare elsewhere.
 */

export const CONTRACT_STATES = [
  "CREATED",
  "SCHEDULED",
  "EXECUTION",
  "ACCOUNTING",
  "PAYMENT",
  "COMPLETED",
  "WITHDRAWN",
  "REJECTED",
] as const;
export type ContractState = (typeof CONTRACT_STATES)[number];

export const CONTRACTS_SORT_KEYS = [
  "contractCode",
  "projectName",
  "siteName",
  "customerName",
  "contractorName",
  "totalAssets",
  "progressPct",
  "state",
] as const;
export type ContractsSortKey = (typeof CONTRACTS_SORT_KEYS)[number];

export type ContractsSortDir = "asc" | "desc";

/** TNCY-4 (projects/v1@2.4.0): which side of the cross-tenant fence a
 *  contract row is on. `owner` = the caller's own contract (default
 *  `scope=own`); `member` = a contract the caller was granted cross-tenant
 *  read access to via a `contract_*` grant (`scope=member`). */
export type ContractOwnership = "owner" | "member";

/** TNCY-4 (2.4.0): read/list scope. `own` (default) = caller's own
 *  contracts; `member` = the cross-tenant fan-out of granted contracts. The
 *  FE fires both and merges them into one list (`useOwnerMemberContracts`). */
export type ContractsScope = "own" | "member";

/** TNCY-4 / XT-D22 — the caller's access role on a `member` contract. Mirrors
 *  the `tenancy/v1` AccessRole enum; null on `owner` rows. */
export const CONTRACT_ACCESS_ROLES = [
  "project_customer",
  "project_contractor",
  "contract_customer",
  "contract_contractor",
  "reporting_subcontractor",
  "ii_subcontractor",
] as const;
export type ContractAccessRole = (typeof CONTRACT_ACCESS_ROLES)[number];

export interface ContractListItem {
  contractId: string;
  contractCode: number;
  projectId: string;
  projectName: string;
  siteId: string | null;
  siteName: string | null;
  customerTenantId: string | null;
  customerTenantName: string | null;
  /** PM-1 (2.7.0, PM-D24) — the descriptive contract-customer contact fallback
   *  (`contracts.customer_contact_id`), polymorphic with `customerTenantId`.
   *  Optional for back-compat with a pre-2.7.0 BE; always present from 2.7.0.
   *  FB-43 — the project-edit contract-party prefill reads it. */
  customerContactId?: string | null;
  contractorTenantId: string | null;
  contractorTenantName: string | null;
  /** PM-1 (2.7.0, PM-D24) — the descriptive contract-contractor contact
   *  fallback (`contracts.contractor_contact_id`). FB-43 prefill source. */
  contractorContactId?: string | null;
  billingContactCompanyName: string | null;
  customerReference: string | null;
  totalAssets: number;
  /** Per Q2 — NULL when contract has zero non-WITHDRAWN/REJECTED
   *  positions. Rendered as em-dash in the cell. */
  progressPct: number | null;
  state: ContractState;
  startDate: string | null;
  endDate: string | null;
  externalIds: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  /** TNCY-4 (2.4.0): `owner` | `member`. Optional for back-compat with a
   *  pre-2.4.0 BE; always present from 2.4.0. Absent ⇒ treated as `owner`. */
  ownership?: ContractOwnership;
  /** TNCY-4: the data-owning tenant's display name on a `member` row; null on
   *  `owner` rows. */
  ownerTenantName?: string | null;
  /** TNCY-4: the caller's access role on a `member` contract (XT-D22);
   *  null on `owner` rows. */
  role?: ContractAccessRole | null;
}

export interface ContractListResponse {
  items: ContractListItem[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Per BE contract (`ContractsFacetOption`) — `{id, label, count?}`.
 * Note this differs from `ProjectsFacetOption` which uses `{id, name}`.
 */
export interface ContractsFacetOption {
  id: string;
  label: string;
  count?: number;
}

export interface ContractsFacetResponse {
  items: ContractsFacetOption[];
}

/**
 * PM-1 (projects/v1) — POST body for `createContract`. Creates a `contracts`
 * row under `projectId`. `contract_code` + `contract_id` + audit columns are
 * BE-managed; `state` defaults to `CREATED`. One contract per park — the
 * park's site rides along as `siteId`. Party `*_tenant_id` fields auto-grant
 * the contract-scoped roles (PM-D24); the `*ContactId` fields are descriptive
 * fallbacks (never grant). The unified-create flow (FE-1) only needs
 * `projectId` + `siteId`; the rest are optional and defaulted server-side.
 */
export interface ContractCreateRequest {
  projectId: string;
  siteId?: string | null;
  customerTenantId?: string | null;
  customerContactId?: string | null;
  contractorTenantId?: string | null;
  contractorContactId?: string | null;
  customerReference?: string | null;
  state?: ContractState;
  startDate?: string | null;
  endDate?: string | null;
  externalIds?: { provider: string; id: string }[];
}

/**
 * FB-43 (projects/v1) — PATCH body for `updateContract`
 * (`PATCH /api/projects/v1/contracts/{contractId}`). Updates a contract's
 * party fields only; every field is OPTIONAL — an absent key leaves that
 * column unchanged, a present key (including explicit `null`) SETS it. The
 * party tenant ids (`customerTenantId` / `contractorTenantId`) auto-grant the
 * contract-scoped role via the same reconcile as `createContract` /
 * `updateProjectParties` (XT-D23 / PM-D23, owner-`tenant_admin`-gated); the
 * `*ContactId` fields are descriptive fallbacks (never grant). A tenant id and
 * its contact id are mutually exclusive per party — the caller sends one and
 * `null`s the other when switching modes. At least one field must be present.
 */
export interface ContractUpdateRequest {
  customerTenantId?: string | null;
  customerContactId?: string | null;
  contractorTenantId?: string | null;
  contractorContactId?: string | null;
}

/** PM-1 — detail row returned by `createContract` (the `201` body). The
 *  unified-create flow only reads `contractId` to link positions under it. */
export interface ContractDetail {
  contractId: string;
  contractCode: number;
  projectId: string;
  projectName: string | null;
  siteId: string | null;
  siteName: string | null;
  customerTenantId: string | null;
  contractorTenantId: string | null;
  state: ContractState;
  externalIds: Record<string, unknown> | unknown[];
  createdAt: string;
  updatedAt: string;
}

export interface ListContractsParams {
  q?: string;
  projectIds?: readonly string[];
  siteIds?: readonly string[];
  customerIds?: readonly string[];
  contractorIds?: readonly string[];
  states?: readonly ContractState[];
  includeCompleted?: boolean;
  sortBy?: ContractsSortKey;
  sortDir?: ContractsSortDir;
  page?: number;
  pageSize?: number;
  /** TNCY-4 (2.4.0): `own` (default, single-schema fast path) | `member`
   *  (cross-tenant fan-out of granted contracts). Omitted ⇒ BE defaults to
   *  `own`. The FE fires both and merges (`useOwnerMemberContracts`). */
  scope?: ContractsScope;
}
